import Paginate from "../paginate/paginate";
import { PropertyCardSkeleton } from "../loading/PropertyCardSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { addProductToCompare, compareItem } from "@/redux/slices/compareSlice";
import { regions } from "@/data/main-data";
import Image from "next/image";
import { trunkPropertyDesc } from "@/utils/trunck";
import Link from "next/link";
import { FaCheck } from "react-icons/fa";
import { LuRefreshCcw } from "react-icons/lu";
import { useLocale } from "next-intl";
import { BedDouble, Bath, Maximize2 } from "lucide-react";
import FavoriteButton from "../properties/FavoriteButton";
import FairPriceSeal from "../properties/FairPriceSeal";

export default function UnitsLayout({
  data,
  isLoading,
  currentPage,
  setCurrentPage,
}: any) {
  const properties = data?.properties || [];
  const dispatch = useDispatch();
  const compare = useSelector(compareItem);
  const locale = useLocale();

  return (
    <div className="flex-1 w-full">
      {isLoading ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, index) => (
            <PropertyCardSkeleton key={index} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4">
          {isLoading ? (
            [...Array(6)].map((_, index) => (
              <PropertyCardSkeleton key={index} />
            ))
          ) : properties?.length === 0 ? (
            <div className="py-20 col-span-full">
              <p className="text-center text-gray-500">
                {locale === "ar"
                  ? "لا توجد عقارات مطابقة للمعايير المحددة."
                  : "No properties match the specified criteria."}
              </p>
            </div>
          ) : (
            properties.map((property: any) => {
              const region = regions.find((e) => e.value === property.region);
              const isSale = property.is_sale === true;
              const findCompare = compare.find(
                (item: any) => item === property.id,
              );

              const rentType = property?.rent_type;

              return (
                <div
                  key={property.id}
                  className="border border-gray-300 rounded-t-xl relative hover:shadow-lg transition-shadow bg-white"
                >
                  <div className="relative">
                    <Image
                      src={property.thumbnail}
                      alt={property.title_en}
                      priority
                      width={400}
                      height={300}
                      quality={90}
                      className="w-full h-auto rounded-t"
                    />
                    {property?.is_sell && (
                      <span className="absolute top-2 left-2 bg-teal-600 text-white text-xs px-2 py-1 rounded select-none">
                        {locale === "ar"
                          ? isSale ? "تم البيع" : "تم التأجير"
                          : isSale ? "Sold Out" : "Rented"}
                      </span>
                    )}
                    {/* favorite heart */}
                    <FavoriteButton
                      propertyId={property.id}
                      className="absolute top-2 right-2"
                    />
                    {/* fair-price seal */}
                    {!property?.is_sell && (
                      <FairPriceSeal fairness={property?.fairness} className="absolute bottom-2 start-2" />
                    )}
                  </div>
                  <div className="p-3">
                    <h2 className="text-lg text-dark font-bold mb-1">
                      {locale === "ar" ? property.title_ar : property.title_en}
                    </h2>
                    <h3 className="text-sm text-gray-500">
                      {locale === "ar" ? region?.label_ar : region?.label_en}
                    </h3>
                    {/* PRICE */}
                    <div className="my-1 flex items-center gap-1">
                      <p className="text-main font-semibold text-xl">
                        {isSale
                          ? property?.sale_price?.toLocaleString()
                          : rentType === "yearly"
                            ? property?.price_per_year?.toLocaleString()
                            : property?.price_per_day?.toLocaleString()}
                      </p>
                      <Image
                        src={`/sar.png`}
                        alt="SAR"
                        quality={90}
                        priority
                        width={13}
                        height={13}
                      />
                      {!isSale && (
                        <span className="text-sm text-gray-500">
                          /{" "}
                          {rentType === "yearly"
                            ? locale === "ar"
                              ? "سنوياً"
                              : "per year"
                            : locale === "ar"
                              ? "يوميا"
                              : "per day"}
                        </span>
                      )}
                    </div>
                    {/* SPECS: bedrooms / bathrooms / area */}
                    <div className="flex items-center gap-4 my-2 text-sm text-gray-600">
                      {property?.bedrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <BedDouble size={16} className="text-main" />
                          {property.bedrooms}
                        </span>
                      )}
                      {property?.bathrooms > 0 && (
                        <span className="flex items-center gap-1">
                          <Bath size={16} className="text-main" />
                          {property.bathrooms}
                        </span>
                      )}
                      {property?.area > 0 && (
                        <span className="flex items-center gap-1">
                          <Maximize2 size={15} className="text-main" />
                          {property.area} {locale === "ar" ? "م²" : "m²"}
                        </span>
                      )}
                    </div>

                    {/* DESCRIPTION */}
                    <p className="text-sm text-gray-600 wrap break-words">
                      {trunkPropertyDesc(
                        locale === "ar"
                          ? property?.description_ar
                          : property?.description_en,
                        100,
                      )}
                    </p>

                    <div className="mt-4 flex items-center gap-2">
                      <Link
                        href={`/properties/${property.slug}`}
                        className="block w-full bg-main text-white text-center py-2 rounded-lg font-medium hover:bg-main/90 transition-colors"
                      >
                        {locale === "ar" ? "احجز الآن" : "Book Now"}
                      </Link>
                      <button
                        className="cursor-pointer bg-main text-white p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() =>
                          dispatch(addProductToCompare(property.id))
                        }
                      >
                        {findCompare ? (
                          <FaCheck className="w-5 h-5" />
                        ) : (
                          <LuRefreshCcw className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      {data?.pagination?.totalPages > 0 && (
        <Paginate
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={data?.pagination?.totalPages || 1}
        />
      )}
    </div>
  );
}
